// libraries
import { extractNumberFromInputValue } from '@makemydeal/dr-shared-ui-utils';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { OdometerMileageTypes } from '@makemydeal/dr-platform-types';

// utils
import { vehicleInfoFormKeys } from './formContext';

// interfaces/types
import type { FMForm, FMGenerator, FMTouchedInputs, FMNames } from '../types';
import { FMField } from '../types';

const isEmpty = (value: unknown) => value === undefined || value === '';

type O = { [key: string]: any };
type isObjectEqualType = (A: O, B?: O) => boolean;

// undefined and '' both read as empty, so clearing an already-empty field is not a change
export const isSameValue = (a: unknown, b: unknown) => a === b || (isEmpty(a) && isEmpty(b));

export const isObjectEqual: isObjectEqualType = (A, B = {}) => {
    for (const key of Object.keys(A)) {
        if (!isSameValue(A[key], B[key])) {
            return false;
        }
    }
    return true;
};

type ValidatedVehicle = Partial<Omit<IVehicle, FMNames>> & Partial<FMGenerator<string | number>>;

type ValidatedForm = {
    retailPrice?: number;
    sellingPrice?: number;
    vehicle: ValidatedVehicle;
};

const STRING_TYPE_VEHICLE_FIELDS: FMNames[] = [
    FMField.exteriorColor,
    FMField.interiorColor,
    FMField.fuel,
    FMField.engine,
    FMField.transmission,
    FMField.transmissionType,
    FMField.trim,
    FMField.brakingMechanism,
    FMField.steeringMechanism,
    FMField.driveLine,
    FMField.vehicleUse,
    FMField.principalDriverName
];

export const validateForm = (form: FMForm, touched: FMTouchedInputs) => {
    const modified: ValidatedForm = { vehicle: {} };
    vehicleInfoFormKeys.forEach((key) => {
        if (touched[key]) {
            if (key === FMField.msrp) {
                modified.retailPrice = extractNumberFromInputValue(form[key] as string);
            } else if (key === FMField.sellingPrice) {
                modified.sellingPrice = extractNumberFromInputValue(form[key] as string);
            } else if (key === FMField.wholesaleBookSource) {
                modified.vehicle.wholesaleBookSource = form[key] as string;
            } else if (key === FMField.wholesaleValueType) {
                modified.vehicle.wholesaleValueType = form[key] as string;
            } else if (key === FMField.stockNumber) {
                modified.vehicle.stockNumber = form[key] as string;
            } else if (key === FMField.cylinders) {
                modified.vehicle.cylinders =
                    form[key]?.toString?.()?.trim?.() === '' ? undefined : extractNumberFromInputValue(form[key] as string);
            } else if (key === FMField.odometerMileageType) {
                modified.vehicle.odometerMileageType = form[key] ? OdometerMileageTypes.Actual : OdometerMileageTypes.Estimate;
            } else if (key === FMField.condition) {
                const condition = form[key] as string;
                modified.vehicle.condition = condition;
                modified.vehicle.type = condition.toLowerCase();
            } else if (key === FMField.isAirConditioningIncluded) {
                (modified.vehicle as any)[key] = form[key] === 'true';
            } else if (STRING_TYPE_VEHICLE_FIELDS.includes(key)) {
                modified.vehicle[key] = form[key] as string;
            } else {
                modified.vehicle[key] = form[key] ? extractNumberFromInputValue(form[key] as string) : undefined;
            }
        }
    });
    const anyPaymentFieldChanged =
        Object.keys(modified).some((key) => ['retailPrice', 'sellingPrice'].includes(key)) ||
        Object.keys(modified.vehicle).some((key) => ['invoiceAmount', 'mileage', 'cost', 'condition'].includes(key));

    return { payload: { ...form, ...modified }, withPayment: anyPaymentFieldChanged };
};

// Bookout Source and Value are required as a pair: either both are set or neither is
export const getMissingBookoutFields = (form: FMForm): FMNames[] => {
    const hasSource = !!form.wholesaleBookSource;
    const hasValue = !!Number(form.wholesaleValue);
    if (hasSource === hasValue) return [];
    return [hasSource ? FMField.wholesaleValue : FMField.wholesaleBookSource];
};

export const odometerValidator = (value: string | number) => {
    const parsed = Math.round(Math.abs(extractNumberFromInputValue(value)));
    return String(parsed > 999_999 ? 999_999 : parsed < 1 ? 1 : parsed);
};

export const gvwrValidator = (value: string | number) => {
    const parsed = Math.abs(extractNumberFromInputValue(value));
    if (parsed > 1_000_000) return String(1_000_000);
    return String(parsed);
};

export const bookOutValueValidator = (value: string | number) => {
    if (value === '') return '';
    const parsed = Math.abs(extractNumberFromInputValue(value));
    return String(parsed > 999_999 ? 999_999 : parsed < 1 ? 0 : parsed);
};

export const pricingInputList: { name: keyof FMForm; label: string }[] = [
    { name: 'msrp', label: 'MSRP/Retail Price' },
    { name: 'sellingPrice', label: 'Selling Price' },
    { name: 'invoiceAmount', label: 'Invoice' },
    { name: 'cost', label: 'Cost' }
];
